import type React from 'react';
import { required } from '@seek/validators-js';
import { TextField, Stack, Box } from 'braid-design-system';
import { observer } from 'mobx-react';

import { useTranslations } from '@vocab/react';
import translations from '../.vocab';
import useValidateExpiryDate from './hooks/useValidateExpiryDate';
import ActionableForm, {
  type ActionableFormInputProps,
} from 'src/components/common/ActionableForm';
import {
  type ActionResult,
  failResult,
  okResult,
} from 'src/components/common/ActionableFormView';
import type { IJob } from 'src/stores/JobStore/Job';

interface ExpiryDateValues {
  expiryDate: string;
}

interface Props {
  job: IJob;
  onSubmitSuccess: (message: string) => void;
}

const UpdateExpiryDateFormFields: React.FC<ActionableFormInputProps> = ({
  useFormField,
}) => {
  const { t } = useTranslations(translations);
  const expiryDate = useFormField({
    id: 'expiryDate',
    validators: [required] as any,
    validatorProps: { requiredLabel: t('expiry date') },
  });

  return (
    <Box>
      <Stack space="medium">
        <TextField
          id="expiry-date"
          label={t('(CS) Expire ad on')}
          value={expiryDate.value}
          tone={expiryDate.valid ? 'neutral' : 'critical'}
          onChange={expiryDate.onChange}
          onBlur={expiryDate.onBlur}
          message={!expiryDate.valid && expiryDate.errorMessage}
          reserveMessageSpace
        />
      </Stack>
    </Box>
  );
};

const UpdateExpiryDateForm = observer(({ job, onSubmitSuccess }: Props) => {
  const { t } = useTranslations(translations);
  const { getValidationExpiryDateMessage } = useValidateExpiryDate();
  const onSubmit = async (
    formValues: ExpiryDateValues,
  ): Promise<ActionResult> => {
    const validationResult = getValidationExpiryDateMessage(
      formValues.expiryDate,
    );

    if (!validationResult.valid) {
      return failResult(validationResult.errorMessage);
    }

    const result = await job.updateExpiryDate(validationResult.expiredDate);

    if (result) {
      return okResult(
        t('Expiry date has been successfully updated', {
          expiryDate: formValues.expiryDate,
        }),
      );
    }

    return failResult(
      t('Sorry, we couldn’t save your changes. Can we try again?'),
    );
  };

  return (
    <Box>
      <ActionableForm
        initialFormValues={{
          expiryDate: job.expiryDate,
        }}
        onSubmit={onSubmit}
        submitButtonLabel={t('Update expiry date')}
        submitButtonLoadingLabel={t('Updating')}
        onSubmitSuccess={onSubmitSuccess}
      >
        <UpdateExpiryDateFormFields />
      </ActionableForm>
    </Box>
  );
});

export default UpdateExpiryDateForm;
